import {
  useState,
  forwardRef,
  type ReactNode,
  type HTMLAttributes,
  type KeyboardEvent,
} from 'react';
import { useTheme } from '@vitness/theme';
import { surface, type SurfaceVariant, type SurfaceDefinition } from '@vitness/tokens';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  variant?: SurfaceVariant;
  padding?: number;
  disabled?: boolean;
}

/**
 * Themed surface container. When `onClick` is provided (and not disabled) the
 * card becomes keyboard-interactive: `role="button"`, focusable, and
 * activatable with Enter/Space — callers can override via props if they
 * compose their own semantics.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { children, variant, padding, disabled, style, onClick, ...props },
  ref,
) {
  const { surface: themeVariant, density } = useTheme();
  const v = variant ?? themeVariant;
  const sf: SurfaceDefinition = surface[v];
  const [hov, setHov] = useState(false);

  const isInteractive = Boolean(onClick) && !disabled;
  const handleKeyDown = isInteractive
    ? (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.currentTarget.click();
        }
      }
    : undefined;

  return (
    <div
      ref={ref}
      onClick={disabled ? undefined : onClick}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov && !disabled ? sf.hoverBackground : sf.background,
        border: hov && !disabled ? (sf.hoverBorder || sf.border) : sf.border,
        boxShadow: hov && !disabled ? sf.hoverShadow : sf.shadow,
        borderRadius: 8,
        padding: padding ?? (density === 'tight' ? 14 : 20),
        position: 'relative',
        transition: 'background 0.2s, border-color 0.2s, box-shadow 0.2s',
        cursor: onClick ? 'pointer' : 'default',
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  );
});
